
// src/components/Drivers.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInstantListCache } from "../hooks/useInstantListCache";
import axios from "../axiosConfig";
import {
  FaSearch,
  FaTrashAlt,
  FaPhoneAlt,
  FaIdCard,
  FaTachometerAlt,
  FaTruck,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaFlag,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { Container } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../styles/Drivers.css";
import DriverNicknameInput from "./DriverNicknameInput";
import { getSocket } from "../lib/socket";
import { getStoredToken, getStoredUser } from "../lib/authStorage";
import {
  Trans,
  useTranslation,
} from "react-i18next";
import {
  primeDriverDetailsCache,
} from "../utils/driverDetailsCache";

const RAW_API_BASE = axios?.defaults?.baseURL || "";
const API_BASE =
  /^https?:\/\//i.test(RAW_API_BASE)
    ? RAW_API_BASE.replace(/\/+$/, "")
    : typeof process !== "undefined" && process.env.REACT_APP_API_ORIGIN
    ? `${process.env.REACT_APP_API_ORIGIN.replace(/\/+$/, "")}/api`
    : "https://api.loadevo.com/api";
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, "").replace(/\/+$/, "");
/* ======================== Helpers: company resolve ======================== */
function decodePayload(tok) {
  try {
    const b64 = tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}
function resolveCompanyId() {
  if (typeof window !== "undefined" && window.__COMPANY_ID__) {
    const v = Number(window.__COMPANY_ID__);
    if (Number.isFinite(v) && v > 0) return v;
  }
  const envCid = Number(
    (typeof process !== "undefined" && process.env.REACT_APP_COMPANY_ID) || 0
  );
  if (Number.isFinite(envCid) && envCid > 0) return envCid;
  const tok = getStoredToken() || localStorage.getItem("authToken") || "";
  const p = tok ? decodePayload(tok) : null;
  const fromJwt = p && (p.company_id ?? p.companyId);
  if (Number.isFinite(Number(fromJwt)) && Number(fromJwt) > 0) {
    return Number(fromJwt);
  }
  const direct =
    sessionStorage.getItem("companyId") ||
    sessionStorage.getItem("company_id") ||
    localStorage.getItem("companyId") ||
    localStorage.getItem("company_id") ||
    null;
  if (Number.isFinite(Number(direct)) && Number(direct) > 0) {
    return Number(direct);
  }
  const storedUser = getStoredUser();
  const fromUser = storedUser?.company_id ?? storedUser?.companyId ?? null;
  if (Number.isFinite(Number(fromUser)) && Number(fromUser) > 0) {
    return Number(fromUser);
  }
  return null;
}
function driversCacheKeyWithCid(cid) {
  const baseKey = cid ? `company:${cid}` : "company:unknown";
  return `loadevo.sidebar.${baseKey}:drivers:summary`;
}

const DRIVER_OPERATIONS_CACHE_MAX_AGE_MS =
  12 * 60 * 60 * 1000;

const driverOperationsMemoryCache = new Map();

function driverOperationsCacheKeyWithCid(cid) {
  const baseKey = cid
    ? `company:${cid}`
    : "company:unknown";

  return `loadevo.${baseKey}:drivers:operations:v3`;
}

function readDriverOperationsCacheSync(cacheKey) {
  const memoryEntry =
    driverOperationsMemoryCache.get(cacheKey);

  if (
    memoryEntry &&
    typeof memoryEntry === "object" &&
    !Array.isArray(memoryEntry)
  ) {
    const memoryTimestamp =
      Number(memoryEntry.ts) || 0;

    const memoryExpired =
      !memoryTimestamp ||
      Date.now() - memoryTimestamp >
        DRIVER_OPERATIONS_CACHE_MAX_AGE_MS;

    if (!memoryExpired) {
      const memoryData =
        memoryEntry.data;

      if (
        memoryData &&
        typeof memoryData === "object" &&
        !Array.isArray(memoryData)
      ) {
        return memoryData;
      }
    }

    driverOperationsMemoryCache.delete(
      cacheKey
    );
  }

  try {
    const raw =
      localStorage.getItem(cacheKey);

    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);

    const timestamp =
      Number(parsed?.ts) || 0;

    const isExpired =
      !timestamp ||
      Date.now() - timestamp >
        DRIVER_OPERATIONS_CACHE_MAX_AGE_MS;

    if (isExpired) {
      localStorage.removeItem(cacheKey);

      driverOperationsMemoryCache.delete(
        cacheKey
      );

      return {};
    }

    const data = parsed?.data;

    if (
      !data ||
      typeof data !== "object" ||
      Array.isArray(data)
    ) {
      localStorage.removeItem(cacheKey);
      return {};
    }

    driverOperationsMemoryCache.set(
      cacheKey,
      {
        ts: timestamp,
        data,
      }
    );

    return data;
  } catch {
    return {};
  }
}

function writeDriverOperationsCache(
  cacheKey,
  data
) {
  if (
    !data ||
    typeof data !== "object" ||
    Array.isArray(data)
  ) {
    return;
  }

  const cacheEntry = {
    ts: Date.now(),
    data,
  };

  driverOperationsMemoryCache.set(
    cacheKey,
    cacheEntry
  );

  try {
    localStorage.setItem(
      cacheKey,
      JSON.stringify(cacheEntry)
    );
  } catch {
    /*
     * A interface continua funcional
     * mesmo sem localStorage.
     */
  }
}

/* ===================== Adapter: DriverOriginal -> UI row ===================== */
function toAbsolutePhotoUrl(u) {
  if (!u) return undefined;
  const s = String(u).trim();
  if (!s) return undefined;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("data:")) return s;
  if (s.startsWith("/driveroriginal/")) {
    return `${API_BASE}${s}`;
  }
  if (s.startsWith("/static/")) {
    return `${API_BASE}${s}`;
  }
  if (s.startsWith("/")) {
    return `${API_ORIGIN}${s}`;
  }
  return `${API_ORIGIN}/${s.replace(/^\/+/, "")}`;
}
function pickUrl(v) {
  if (!v) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    if (v.url) return v.url;
    if (v.uri) return v.uri;
  }
  return undefined;
}
function valueToDisplayText(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") {
    return String(v).trim();
  }
  if (Array.isArray(v)) {
    return v.map(valueToDisplayText).filter(Boolean).join(", ");
  }
  if (typeof v === "object") {
    return (
      v.plate ||
      v.truckPlate ||
      v.truck_plate ||
      v.trailerPlate ||
      v.trailer_plate ||
      v.trailerNumber ||
      v.name ||
      v.label ||
      v.value ||
      ""
    );
  }
  return "";
}
function joinUniqueDisplayValues(...values) {
  const parts = [];
  for (const value of values) {
    const text = valueToDisplayText(value);
    if (!text) continue;
    text
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .forEach((x) => parts.push(x));
  }
  return [...new Set(parts)].join(", ");
}
function formatDriverPointDateTime(date, hour) {
  const cleanDate = date ? String(date).trim() : "";
  const cleanHour = hour ? String(hour).trim().slice(0, 5) : "";
  let formattedDate = "";
  if (cleanDate) {
    const parsed = new Date(`${cleanDate}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      formattedDate = parsed
        .toLocaleDateString("pt-PT", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(".", "");
    } else {
      formattedDate = cleanDate;
    }
  }
  return [formattedDate, cleanHour].filter(Boolean).join(" • ");
}
function formatDriverJourneyDateTime(date, hour) {
  const cleanDate = date ? String(date).trim() : "";
  const cleanHour = hour ? String(hour).trim().slice(0, 5) : "";
  if (!cleanDate && !cleanHour) return "";
  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);
  let dateLabel = cleanDate;
  if (cleanDate === todayKey) {
    dateLabel = "Hoje";
  } else if (cleanDate) {
    const parsed = new Date(`${cleanDate}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      dateLabel = parsed
        .toLocaleDateString("pt-PT", {
          day: "2-digit",
          month: "short",
        })
        .replace(".", "");
    }
  }
  return [dateLabel, cleanHour].filter(Boolean).join(", ");
}
function compareOperationDateTime(a, b) {
  const aKey = `${a.date || "9999-12-31"}T${a.hour || "23:59"}`;
  const bKey = `${b.date || "9999-12-31"}T${b.hour || "23:59"}`;
  return aKey.localeCompare(bKey);
}
const DRIVER_LOAD_TONE_CLASSES = [
  "drivers-load-tone--blue",
  "drivers-load-tone--purple",
  "drivers-load-tone--orange",
  "drivers-load-tone--green",
  "drivers-load-tone--cyan",
  "drivers-load-tone--gray",
];
function getDriverLoadToneClass(loadId, index = 0) {
  const numericLoadId = Number(loadId);
  const source = Number.isFinite(numericLoadId) && numericLoadId > 0 ? numericLoadId : index + 1;
  return DRIVER_LOAD_TONE_CLASSES[Math.abs(source) % DRIVER_LOAD_TONE_CLASSES.length];
}
function getDriverLoadBadgeLabel(load, index = 0) {
  const loadId = Number(load?.loadId);
  if (Number.isFinite(loadId) && loadId > 0) {
    return `Carga ${loadId}`;
  }
  if (load?.loadNumber) {
    return `Carga ${load.loadNumber}`;
  }
  return `Carga ${index + 1}`;
}
function normalizeDriverOperationStatus(value, options = {}) {
  const { source = "point" } = options;

  const raw = String(value || "").trim();
  const normalized = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s-]+/g, "_");

  if (!normalized) return "";

  if (
    normalized === "completed" ||
    normalized === "complete" ||
    normalized === "concluida" ||
    normalized === "concluido"
  ) {
    return "COMPLETED";
  }

  if (
    normalized === "cancelled" ||
    normalized === "canceled" ||
    normalized === "cancelada" ||
    normalized === "cancelado"
  ) {
    return "CANCELLED";
  }

  if (
    normalized === "pending" ||
    normalized === "pendente" ||
    normalized === "waiting"
  ) {
    return "PENDING";
  }

  if (
    normalized === "current" ||
    normalized === "active" ||
    normalized === "open"
  ) {
    return source === "load" ? "PENDING" : "PENDING";
  }

  if (
    normalized === "in_transit" ||
    normalized === "intransit" ||
    normalized === "transit" ||
    normalized === "em_transito" ||
    normalized === "in_progress" ||
    normalized === "progress"
  ) {
    return "IN_TRANSIT";
  }

  return raw.toUpperCase();
}

function getDriverStatusLabel(status, t) {
  const key = normalizeDriverOperationStatus(status);

  if (!key) {
    return t("status.pending", "Pendente");
  }

  return t(`status_selector_modal:statuses.${key}`, {
    defaultValue: t(`status_selector_modal:subOptions.${key}`, {
      defaultValue: t(`status_selector_modal:subSubOptions.${key}`, {
        defaultValue: t(`status_selector_modal:subSubSubOptions.${key}`, {
          defaultValue: key,
        }),
      }),
    }),
  });
}

function getDriverStatusFamily(status) {
  const key = normalizeDriverOperationStatus(status);

  if (key === "COMPLETED") return "completed";
  if (key === "CANCELLED" || key === "CANCELED") return "cancelled";
  if (key === "PENDING") return "pending";

  if (
    key === "IN_TRANSIT" ||
    key === "IN_TRANSIT_MOVING_TO_PICKUP_LOCATION" ||
    key === "IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION" ||
    key === "MOVING_TO_PICKUP_LOCATION" ||
    key === "MOVING_TO_DELIVERY_LOCATION" ||
    key === "DELAYED" ||
    key.startsWith("DELAYED_BY_")
  ) {
    return "transit";
  }

  if (
    key === "AT_PICKUP_LOCATION" ||
    key === "AT_DELIVERY_LOCATION" ||
    key === "WAITING_TO_LOAD" ||
    key === "LOADING_IN_PROGRESS" ||
    key === "WAITING_TO_UNLOAD" ||
    key === "UNLOADING_IN_PROGRESS" ||
    key === "WAITING_FOR_MERCHANDISE" ||
    key === "WAITING_DELAYED" ||
    key === "WAITING_FOR_UNLOADING" ||
    key === "LOADING_MERCHANDISE" ||
    key === "LOADING_DELAYED" ||
    key === "UNLOADING_MERCHANDISE" ||
    key === "UNLOADING_DELAYED" ||
    key.startsWith("WAITING_DELAYED_") ||
    key.startsWith("LOADING_DELAYED_") ||
    key.startsWith("UNLOADING_DELAYED_")
  ) {
    return "onsite";
  }

  return "pending";
}

function getDriverOperationStatusMeta(operation, t) {
  const rawStatus = normalizeDriverOperationStatus(
    operation?.pointStatus ||
      operation?.operationStatus ||
      operation?.status ||
      operation?.effectiveStatus ||
      operation?.rawStatus ||
      operation?.loadStatus ||
      ""
  );

  const statusFamily = getDriverStatusFamily(rawStatus);

  if (statusFamily === "completed") {
    return {
      statusLabel: getDriverStatusLabel(rawStatus, t),
      statusSubtitle: t("assignment.operationCompleted", "Operação concluída"),
      statusClassName: "drivers-operation-status-pill--completed",
      statusIcon: <FaCheckCircle />,
    };
  }

  if (statusFamily === "cancelled") {
    return {
      statusLabel: getDriverStatusLabel(rawStatus, t),
      statusSubtitle: t("assignment.operationCancelled", "Operação cancelada"),
      statusClassName: "drivers-operation-status-pill--cancelled",
      statusIcon: <FaTimesCircle />,
    };
  }

  if (statusFamily === "transit") {
    return {
      statusLabel: getDriverStatusLabel(rawStatus, t),
      statusSubtitle: t("assignment.onWayToDestination", "A caminho do destino"),
      statusClassName: "drivers-operation-status-pill--transit",
      statusIcon: <FaTruck />,
    };
  }

  if (statusFamily === "onsite") {
    return {
      statusLabel: getDriverStatusLabel(rawStatus, t),
      statusSubtitle: t("assignment.onSite", "No local"),
      statusClassName: "drivers-operation-status-pill--transit",
      statusIcon: <FaMapMarkerAlt />,
    };
  }

  return {
    statusLabel: getDriverStatusLabel(rawStatus || "PENDING", t),
    statusSubtitle: t("assignment.awaitingOperation", "A aguardar operação"),
    statusClassName: "drivers-operation-status-pill--pending",
    statusIcon: <FaCalendarAlt />,
  };
}

function getDriverInitials(name) {
  const clean = String(name || "").trim();
  if (!clean) return "DR";
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function mapDriverOriginalToUI(row) {
  const firstDLPhoto =
    Array.isArray(row?.driver_license_photo) && row.driver_license_photo.length > 0
      ? pickUrl(row.driver_license_photo[0]) || row.driver_license_photo[0]
      : undefined;
  const rawProfile =
    row?.profile_photo_url ?? row?.profile_photo ?? row?.profilePhoto ?? null;
  let profileUrlRaw;
  if (Array.isArray(rawProfile)) {
    profileUrlRaw = pickUrl(rawProfile[0]) || rawProfile[0];
  } else if (typeof rawProfile === "object" && rawProfile !== null) {
    profileUrlRaw = pickUrl(rawProfile);
  } else {
    profileUrlRaw = rawProfile;
  }

  const finalProfileUrl =
    toAbsolutePhotoUrl(profileUrlRaw) || toAbsolutePhotoUrl(firstDLPhoto) || undefined;
  const assignedTruck = joinUniqueDisplayValues(
    row.assigned_trucks,
    row.assignedTrucks,
    row.assigned_truck,
    row.assignedTruck,
    row.truckPlates,
    row.truckPlate,
    row.truck_plate,
    row.truck,
    row.trucks,
    row.truckPickupInfo,
    row.truckDeliveryInfo,
    row.currentTruck,
    row.assignedTruckInfo
  );

  const assignedTrailer = joinUniqueDisplayValues(
    row.assigned_trailers,
    row.assignedTrailers,
    row.assigned_trailer,
    row.assignedTrailer,
    row.trailerPlates,
    row.trailerPlate,
    row.trailer_plate,
    row.trailer,
    row.trailers,
    row.trailerPickupInfo,
    row.trailerDeliveryInfo,
    row.currentTrailer,
    row.assignedTrailerInfo
  );

  return {
    /*
     * Mantém todos os campos completos recebidos pelo summary.
     * Os campos abaixo acrescentam apenas os aliases necessários
     * aos cards da página Drivers.
     */
    ...row,

    id: row.id,
    name: row.fullname || row.nickname || row.name || "",
    contact_number: row.phonenumber || row.contact_number || "",
    driving_license_number:
      row.driving_license_number ||
      row.drivingLicenseNumber ||
      row.driver_license ||
      row.driverLicense ||
      "",
    driver_license:
      row.driving_license_number ||
      row.drivingLicenseNumber ||
      row.driver_license ||
      row.driverLicense ||
      "",
    license_country: row.license_country || row.licenseCountry || "",
    license_categories: Array.isArray(row.license_categories)
      ? row.license_categories
      : row.license_categories
      ? [row.license_categories]
      : [],
    tacho_card_number:
      row.tacho_card_number ||
      row.tachoCardNumber ||
      row.tachograph_card_number ||
      row.tachographCardNumber ||
      row.driver_tacho_number ||
      row.driverTachoNumber ||
      "",
    driver_tacho_number:
      row.tacho_card_number ||
      row.tachoCardNumber ||
      row.tachograph_card_number ||
      row.tachographCardNumber ||
      row.driver_tacho_number ||
      row.driverTachoNumber ||
      "",
    expiry_date_tacho_card: row.expiry_date_tacho_card || row.expiryDateTachoCard || "",
    profile_photo_url: finalProfileUrl,
    currentLoads: row.currentLoads ?? row.current_loads ?? 0,
    assigned_truck: assignedTruck,
    assigned_trailer: assignedTrailer,
    profile_photo: row?.profile_photo ?? row?.profilePhoto ?? null,
  };
}

function buildEditPrefillFromListRow(d) {
  return {
    name: d?.name || "",
    fullname: d?.name || "",
    contact_number: d?.contact_number || "",
    phonenumber: d?.contact_number || "",
    profile_photo: d?.profile_photo_url || d?.profile_photo || null,
    date_of_birth: "",
    birth_date: "",
    driver_id: "",
    DriverIDExpiry: "",
    driving_license_number: d?.driving_license_number || d?.driver_license || "",
    driver_license: d?.driver_license || d?.driving_license_number || "",
    license_country: d?.license_country || "",
    license_categories: Array.isArray(d?.license_categories)
      ? d.license_categories
      : d?.license_categories
      ? [d.license_categories]
      : [],
    DriverLicenseExpiry: "",
    tacho_card_number: d?.tacho_card_number || d?.driver_tacho_number || "",
    driver_tacho_number: d?.driver_tacho_number || d?.tacho_card_number || "",
    expiry_date_tacho_card: d?.expiry_date_tacho_card || "",
    id_number: "",
    currently_assigned_truck: d?.assigned_truck || "",
    currently_assigned_trailer: d?.assigned_trailer || "",
    driver_id_photo: null,
    driver_license_photo: null,
    driver_tacho_card_photo: null,
    email: "",
  };
}
function readDriversCacheSync(cacheKey) {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.items)) return parsed.items;
    if (Array.isArray(parsed?.data?.items)) return parsed.data.items;
    if (Array.isArray(parsed?.rows)) return parsed.rows;
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}
function normalizeCachedDrivers(items) {
  const arr = Array.isArray(items) ? items : [];
  if (arr.length === 0) return [];
  return arr.map((item) => {
    if (!item || typeof item !== "object") return item;
    const looksLikeUI =
      Object.prototype.hasOwnProperty.call(item, "contact_number") ||
      Object.prototype.hasOwnProperty.call(item, "driver_license") ||
      Object.prototype.hasOwnProperty.call(item, "driver_tacho_number");
    if (looksLikeUI) {
      return {
        ...item,
        profile_photo_url: toAbsolutePhotoUrl(
          item.profile_photo_url || item.profile_photo || item.profilePhoto
        ),
      };
    }
    return mapDriverOriginalToUI(item);
  });
}

/* ================================ Components ================================ */
function DriverAvatar({
  src,
  name,
  isAvailable,
  eager = false,
}) {
  const photoUrl =
    String(
      src ||
        ""
    ).trim();

  const initials =
    getDriverInitials(
      name
    );

  const [
    failed,
    setFailed,
  ] = React.useState(
    false
  );

  /*
   * Se o URL mudar depois de uma atualização,
   * volta a tentar apresentar a fotografia.
   */
  React.useEffect(
    () => {
      setFailed(
        false
      );
    },
    [
      photoUrl,
    ]
  );

  const shouldShowImage =
    Boolean(
      photoUrl
    ) &&
    !failed;

  return (
    <div className="drivers-avatar-shell">
      <div className="drivers-photo-wrap">
        {shouldShowImage ? (
          <img
            src={
              photoUrl
            }
            alt={
              name ||
              ""
            }
            className="drivers-photo"
            loading={
              eager
                ? "eager"
                : "lazy"
            }
            fetchPriority={
              eager
                ? "high"
                : "auto"
            }
            decoding={
              eager
                ? "sync"
                : "async"
            }
            draggable={false}
            onError={() => {
              setFailed(
                true
              );
            }}
          />
        ) : (
          <span className="drivers-avatar-initials">
            {initials}
          </span>
        )}
      </div>

      <span
        className={`drivers-avatar-presence ${
          isAvailable
            ? "drivers-avatar-presence--available"
            : "drivers-avatar-presence--transit"
        }`}
        aria-hidden="true"
      />
    </div>
  );
}

function Drivers() {
  const { t } = useTranslation(["drivers", "status_selector_modal"]);
  const companyId = useMemo(() => resolveCompanyId(), []);

  const cacheKey = useMemo(
    () => driversCacheKeyWithCid(companyId),
    [companyId]
  );

  const operationsCacheKey = useMemo(
    () =>
      driverOperationsCacheKeyWithCid(
        companyId
      ),
    [companyId]
  );

  const initialCachedOperations = useMemo(
    () =>
      readDriverOperationsCacheSync(
        operationsCacheKey
      ),
    [operationsCacheKey]
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const items = Array.isArray(parsed?.items)
        ? parsed.items
        : Array.isArray(parsed)
        ? parsed
        : [];
      const hasBrokenPhotoShape = items.some(
        (item) =>
          item &&
          typeof item === "object" &&
          (Object.prototype.hasOwnProperty.call(item, "contact_number") ||
            Object.prototype.hasOwnProperty.call(item, "driver_license")) &&
          !item.profile_photo_url &&
          (item.profile_photo || item.profilePhoto)
      );
      if (hasBrokenPhotoShape) {
        localStorage.removeItem(cacheKey);
      }
    } catch {
      localStorage.removeItem(cacheKey);
    }
  }, [cacheKey]);
  const initialCachedDrivers = useMemo(() => {
    const raw = readDriversCacheSync(cacheKey);
    return normalizeCachedDrivers(raw);
  }, [cacheKey]);
  const {
    items: drivers,
    setItems: setDrivers,
    loading: loadingDrivers,
    error: driversError,
    refresh: refreshDrivers,
  } = useInstantListCache({
    cacheKey,
    storage: "local",
    initialItems: initialCachedDrivers,
    enabled: true,
    fetcher: async ({ signal }) => {
      const { data } = await axios.get("/driveroriginal/summary", {
        signal,
        params: { company_id: companyId ?? undefined, limit: 500 },
      });
      const rows = Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data)
        ? data
        : null;

      if (!rows) {
        return {
          items: initialCachedDrivers || [],
          meta: {},
        };
      }

      rows.forEach((row) => {
        const id =
          row?.id ??
          row?.driverId ??
          row?.driver_id;

        if (!id) {
          return;
        }

        /*
         * Guarda os dados originais recebidos
         * do backend antes de os adaptar para
         * os cards da página Drivers.
         */
        primeDriverDetailsCache(
          id,
          {
            ...row,

            __driverDetailsComplete:
              true,
          }
        );
      });

      return {
        items: rows.map(
          mapDriverOriginalToUI
        ),
        meta: {},
      };
    },
  });

  /*
   * useInstantListCache pode devolver uma nova referência
   * de refresh entre renders.
   *
   * Guardamo-la numa ref para que os callbacks de socket
   * não sejam recriados continuamente.
   */
  const refreshDriversRef = useRef(
    refreshDrivers
  );

  useEffect(() => {
    refreshDriversRef.current =
      refreshDrivers;
  }, [refreshDrivers]);

  const stableDriversRef = useRef(
    initialCachedDrivers
  );

  useEffect(() => {
    if (
      Array.isArray(drivers) &&
      drivers.length > 0
    ) {
      stableDriversRef.current = drivers;
    }
  }, [drivers]);

  const driversForRender =
    Array.isArray(drivers) &&
    drivers.length > 0
      ? drivers
      : loadingDrivers
      ? stableDriversRef.current
      : [];

  const [searchTerm, setSearchTerm] = useState("");

  const [
    driverLoadLabelsById,
    setDriverLoadLabelsById,
  ] = useState(
    () => initialCachedOperations
  );

  const [
    driverOperationsRefreshKey,
    setDriverOperationsRefreshKey,
  ] = useState(0);

  const driversPageRefreshTimerRef =
    useRef(null);

  const scheduleDriversPageRefresh =
    useCallback(
      (delayMs = 250) => {
        if (
          driversPageRefreshTimerRef.current
        ) {
          clearTimeout(
            driversPageRefreshTimerRef.current
          );
        }

        driversPageRefreshTimerRef.current =
          setTimeout(() => {
            driversPageRefreshTimerRef.current =
              null;

            /*
             * Atualiza as operações.
             */
            setDriverOperationsRefreshKey(
              (current) => current + 1
            );

            /*
             * Atualiza também os dados gerais
             * dos motoristas sem tornar este
             * callback dependente da função
             * devolvida pelo hook.
             */
            refreshDriversRef.current
              ?.()
              .catch(() => {});
          }, Math.max(0, Number(delayMs) || 0));
      },
      []
    );

  const [errorMessage, setErrorMessage] = useState("");
  const [nickOpen, setNickOpen] = useState(false);
  const [nickLoading, setNickLoading] = useState(false);
  const [nickError, setNickError] = useState("");
  const [nickSuccess, setNickSuccess] = useState("");

  const [
    driverPendingRemoval,
    setDriverPendingRemoval,
  ] = useState(null);

  const [
    deleteConfirmation,
    setDeleteConfirmation,
  ] = useState("");

  const [
    deleteLoading,
    setDeleteLoading,
  ] = useState(false);

  const [
    deleteError,
    setDeleteError,
  ] = useState("");

  const deleteConfirmationInputRef =
    useRef(null);

  const deleteConfirmationMatches =
    deleteConfirmation.trim() === "DELETE";

  const closeDriverDeleteModal =
    useCallback(() => {
      if (deleteLoading) {
        return;
      }

      setDriverPendingRemoval(null);
      setDeleteConfirmation("");
      setDeleteError("");
    }, [deleteLoading]);

  const PAGE_SIZE =
    20;

  const [
    visibleLimit,
    setVisibleLimit,
  ] = useState(
    PAGE_SIZE
  );

  const sentinelRef =
    useRef(
      null
    );

  const tableRef =
    useRef(
      null
    );

  const isSearching =
    (
      searchTerm ||
      ""
    ).trim().length >
    0;

  const [
    baseTableRendered,
    setBaseTableRendered,
  ] = useState(
    () => {
      return (
        Array.isArray(
          initialCachedDrivers
        ) &&
        initialCachedDrivers.length >
          0
      );
    }
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (!nickSuccess) return;
    const tmr = setTimeout(() => setNickSuccess(""), 2500);
    return () => clearTimeout(tmr);
  }, [nickSuccess]);

    useEffect(() => {
    if (!driverPendingRemoval) {
      return undefined;
    }

    const previousBodyOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const focusTimer =
      window.setTimeout(() => {
        deleteConfirmationInputRef.current
          ?.focus();
      }, 80);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDriverDeleteModal();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.clearTimeout(focusTimer);

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );

      document.body.style.overflow =
        previousBodyOverflow;
    };
  }, [
    driverPendingRemoval,
    closeDriverDeleteModal,
  ]);

  useEffect(() => {
    if (!driversError) return;
    setErrorMessage(t("errors.loadFailed", "Falha ao carregar os motoristas."));
  }, [driversError, t]);

  useEffect(() => {
    let cancelled = false;

    const controller =
      new AbortController();

    async function fetchDriverLoadLabels() {
      try {
        const { data } = await axios.get(
          "/pickupdeliveries/driver-blocking",
          {
            signal: controller.signal,
          }
        );
        const rows = Array.isArray(data) ? data : [];
        const next = {};
        rows.forEach((row) => {
          const driverId = Number(row.driverId);
          if (!Number.isFinite(driverId)) return;

          const loadMap = new Map();
          const blocks = Array.isArray(row.sessionBlocks)
            ? row.sessionBlocks
            : [];

          const pointToCardData = (point, block = null) => {
            const label = String(
              point?.location ||
                point?.placeName ||
                point?.pickupLocation ||
                point?.deliveryLocation ||
                point?.address ||
                ""
            ).trim();

            const date =
              point?.date ||
              point?.pickupDate ||
              point?.deliveryDate ||
              "";

            const hour =
              point?.hour ||
              point?.pickupHour ||
              point?.deliveryHour ||
              "";

            const type = String(
              point?.type ||
                point?.pointType ||
                ""
            ).toUpperCase();

            const pointStatus = normalizeDriverOperationStatus(
              point?.pointStatus ||
                point?.operationStatus ||
                point?.status ||
                "",
              { source: "point" }
            );

            const loadStatus = normalizeDriverOperationStatus(
              point?.loadStatus ||
                block?.loadStatus ||
                "",
              { source: "load" }
            );

            const effectiveStatus =
              pointStatus ||
              normalizeDriverOperationStatus(
                point?.effectiveStatus ||
                  point?.rawStatus ||
                  point?.status ||
                  "",
                { source: "point" }
              ) ||
              loadStatus ||
              "PENDING";

            return {
              id: Number(point?.id) || null,
              type,
              label,
              date,
              hour,
              sequence:
                point?.sequence === null ||
                point?.sequence === undefined
                  ? null
                  : Number(point.sequence),

              pointStatus,
              loadStatus,
              effectiveStatus,

              status: effectiveStatus,
              rawStatus: effectiveStatus,

              dateTimeLabel: formatDriverPointDateTime(
                date,
                hour
              ),

              loadId:
                Number(
                  point?.loadId ||
                    block?.loadId
                ) || null,

              loadNumber:
                point?.loadNumber ||
                point?.loadRef ||
                block?.loadNumber ||
                block?.loadRef ||
                "",

              clientName:
                point?.clientName ||
                point?.client ||
                block?.clientName ||
                block?.client ||
                "",
            };
          };

          /*
           * Backend novo:
           * usa row.operations, que contém inclusive pontos sem data/hora.
           *
           * Backend antigo:
           * mantém fallback para sessionBlocks durante o deployment.
           */
          const sourceEntries = Array.isArray(row.operations)
            ? row.operations.map((point) => ({
                point,
                block: null,
              }))
            : blocks.flatMap((block) => {
                const points = Array.isArray(block.points)
                  ? block.points
                  : [];

                return points.map((point) => ({
                  point,
                  block,
                }));
              });

          sourceEntries.forEach(({ point, block }) => {
            const normalizedPoint = pointToCardData(
              point,
              block
            );

            const loadId = Number(
              normalizedPoint.loadId
            );

            if (!normalizedPoint.label) return;

            if (
              !Number.isFinite(loadId) ||
              loadId <= 0
            ) {
              return;
            }

            const current = loadMap.get(loadId) || {
              loadId,
              loadNumber:
                normalizedPoint.loadNumber || "",
              clientName:
                normalizedPoint.clientName || "",
              points: [],
            };

            if (
              !current.loadNumber &&
              normalizedPoint.loadNumber
            ) {
              current.loadNumber =
                normalizedPoint.loadNumber;
            }

            if (
              !current.clientName &&
              normalizedPoint.clientName
            ) {
              current.clientName =
                normalizedPoint.clientName;
            }

            const pointKey = normalizedPoint.id
              ? `id:${normalizedPoint.id}`
              : [
                  normalizedPoint.type,
                  normalizedPoint.label,
                  normalizedPoint.date,
                  normalizedPoint.hour,
                  normalizedPoint.sequence ?? "",
                ].join("|");

            const alreadyExists =
              current.points.some(
                (existingPoint) =>
                  existingPoint.__pointKey ===
                  pointKey
              );

            if (!alreadyExists) {
              current.points.push({
                ...normalizedPoint,
                __pointKey: pointKey,
              });
            }

            loadMap.set(loadId, current);
          });

          next[driverId] = [
            ...loadMap.values(),
          ]
            .map((load) => {
              const points = load.points
                .map(
                  ({
                    __pointKey,
                    ...point
                  }) => point
                )
                .sort((a, b) => {
                  const dateCompare =
                    compareOperationDateTime(a, b);

                  if (dateCompare !== 0) {
                    return dateCompare;
                  }

                  const aSequence =
                    a.sequence === null ||
                    a.sequence === undefined
                      ? Number.MAX_SAFE_INTEGER
                      : Number(a.sequence);

                  const bSequence =
                    b.sequence === null ||
                    b.sequence === undefined
                      ? Number.MAX_SAFE_INTEGER
                      : Number(b.sequence);

                  if (
                    aSequence !== bSequence
                  ) {
                    return (
                      aSequence - bSequence
                    );
                  }

                  return (
                    Number(a.id || 0) -
                    Number(b.id || 0)
                  );
                });

              const pickupPoint =
                points.find(
                  (point) =>
                    point.type === "PICKUP"
                ) || null;

              const deliveryPoints =
                points.filter(
                  (point) =>
                    point.type === "DELIVERY"
                );

              const deliveryPoint =
                deliveryPoints[
                  deliveryPoints.length - 1
                ] || null;

              const fallbackPoint =
                points[0] || null;

              return {
                ...load,

                pickupLabel:
                  pickupPoint?.label || "",
                pickupDate:
                  pickupPoint?.date || "",
                pickupHour:
                  pickupPoint?.hour || "",
                pickupDateTimeLabel:
                  pickupPoint?.dateTimeLabel ||
                  "",

                deliveryLabel:
                  deliveryPoint?.label || "",
                deliveryDate:
                  deliveryPoint?.date || "",
                deliveryHour:
                  deliveryPoint?.hour || "",
                deliveryDateTimeLabel:
                  deliveryPoint?.dateTimeLabel ||
                  "",

                fallbackLabel:
                  fallbackPoint?.label || "",
                fallbackDate:
                  fallbackPoint?.date || "",
                fallbackHour:
                  fallbackPoint?.hour || "",
                fallbackDateTimeLabel:
                  fallbackPoint?.dateTimeLabel ||
                  "",

                points,
              };
            })
            .sort((a, b) => {
              const aPoint =
                a.points[0] || {};

              const bPoint =
                b.points[0] || {};

              const dateCompare =
                compareOperationDateTime(
                  aPoint,
                  bPoint
                );

              if (dateCompare !== 0) {
                return dateCompare;
              }

              return (
                Number(a.loadId) -
                Number(b.loadId)
              );
            });
        });
        if (!cancelled) {
          writeDriverOperationsCache(
            operationsCacheKey,
            next
          );

          setDriverLoadLabelsById(next);
        }
      } catch (err) {
        const wasCancelled =
          cancelled ||
          err?.code === "ERR_CANCELED" ||
          err?.name === "CanceledError" ||
          err?.name === "AbortError";

        if (wasCancelled) {
          return;
        }

        console.warn(
          "[Drivers] failed to fetch driver load labels",
          {
            status: err?.response?.status,
            data: err?.response?.data,
            message: err?.message,
          }
        );
      }
    }
    fetchDriverLoadLabels();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    operationsCacheKey,
    driverOperationsRefreshKey,
  ]);

  /* ================================ Socket ================================ */
  useEffect(() => {
    const s = getSocket();

    const joinRoom = () => {
      const numericCompanyId =
        Number(companyId);

      if (
        Number.isFinite(numericCompanyId) &&
        numericCompanyId > 0
      ) {
        s.emit("companies:join", {
          companyId: numericCompanyId,
        });
      }
    };

    const handleConnect = () => {
      /*
       * Na ligação inicial apenas entra na sala.
       *
       * O carregamento inicial já é feito pelos
       * efeitos HTTP da página, por isso não
       * iniciamos aqui outro refresh.
       */
      joinRoom();
    };

    const handleReconnect = () => {
      /*
       * Depois de uma verdadeira reconexão,
       * entra novamente na sala e confirma
       * se perdeu atualizações.
       */
      joinRoom();
      scheduleDriversPageRefresh(250);
    };

    const onRealtimeChange = () => {
      scheduleDriversPageRefresh(250);
    };

    const onAccepted = () => {
      scheduleDriversPageRefresh(0);
    };

    const realtimeEvents = [
      "drivers:updated",
      "drivers:operations:stale",
      "loads:rev",
      "loads:stale",
      "load:updated",
      "load:points:stale",
      "pd:updated",
      "pd:deleted",
      "pd:created",
      "session:points:stale",
      "truck-fuel-logs:updated",
      "dashboard:invalidate",
    ];

    s.on(
      "connect",
      handleConnect
    );

    s.io?.on?.(
      "reconnect",
      handleReconnect
    );

    s.on(
      "drivers:invitationAccepted",
      onAccepted
    );

    realtimeEvents.forEach(
      (eventName) => {
        s.on(
          eventName,
          onRealtimeChange
        );
      }
    );

    if (s.connected) {
      /*
       * Se o socket já estava ligado quando a
       * página abriu, apenas entra na sala.
       */
      joinRoom();
    }

    return () => {
      s.off(
        "drivers:invitationAccepted",
        onAccepted
      );

      realtimeEvents.forEach(
        (eventName) => {
          s.off(
            eventName,
            onRealtimeChange
          );
        }
      );

      s.off(
        "connect",
        handleConnect
      );

      s.io?.off?.(
        "reconnect",
        handleReconnect
      );
    };
  }, [
    companyId,
    scheduleDriversPageRefresh,
  ]);

  /* ============================= Fetch & Poll ============================= */
  /*
   * useInstantListCache já realiza a validação
   * inicial da lista.
   *
   * O fallback periódico das operações, definido
   * mais abaixo, atualiza também os dados gerais
   * através de scheduleDriversPageRefresh().
   */
  useEffect(() => {
    if (!loadingDrivers) {
      setBaseTableRendered(true);
    }
  }, [loadingDrivers]);
  /* ============================== Derived UI ============================== */
  const filteredDrivers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    if (!q) {
      return driversForRender;
    }

    return driversForRender.filter(
      (driver) =>
        (driver.name || "")
          .toLowerCase()
          .includes(q)
    );
  }, [driversForRender, searchTerm]);

  const totalCount =
    driversForRender.length;
  const visibleCount = filteredDrivers.length;
  const renderedCount = isSearching
    ? visibleCount
    : Math.min(visibleLimit, visibleCount);
  const visibleDrivers = useMemo(() => {
    return isSearching ? filteredDrivers : filteredDrivers.slice(0, visibleLimit);
  }, [filteredDrivers, visibleLimit, isSearching]);
  useEffect(() => {
    if (!isSearching) setVisibleLimit(PAGE_SIZE);
  }, [searchTerm, isSearching]);

    useEffect(() => {
    let pollingTimer = null;

    const startPolling = () => {
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }

      /*
       * O WebSocket continua a ser o mecanismo
       * principal.
       *
       * Este intervalo é apenas uma rede de
       * segurança caso algum evento se perca.
       */
      const intervalMs =
        document.hidden
          ? 2 * 60 * 1000
          : 45 * 1000;

      pollingTimer = setInterval(
        () => {
          scheduleDriversPageRefresh(0);
        },
        intervalMs
      );
    };

    const handleVisibilityChange = () => {
      startPolling();

      if (!document.hidden) {
        scheduleDriversPageRefresh(0);
      }
    };

    const handleWindowFocus = () => {
      scheduleDriversPageRefresh(0);
    };

    const handleOnline = () => {
      scheduleDriversPageRefresh(0);
    };

    const handlePageShow = () => {
      scheduleDriversPageRefresh(0);
    };

    const handleStorage = (event) => {
      if (
        event.key !==
          operationsCacheKey ||
        !event.newValue
      ) {
        return;
      }

      try {
        const parsed =
          JSON.parse(event.newValue);

        const nextData =
          parsed?.data;

        if (
          nextData &&
          typeof nextData === "object" &&
          !Array.isArray(nextData)
        ) {
          /*
           * Outra aba já recebeu e guardou
           * os novos dados. Aplicamos sem
           * fazer outro pedido HTTP.
           */
          setDriverLoadLabelsById(
            nextData
          );
        }
      } catch {
        /*
         * Ignora cache inválida e deixa
         * a próxima revalidação corrigi-la.
         */
      }
    };

    startPolling();

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    window.addEventListener(
      "focus",
      handleWindowFocus
    );

    window.addEventListener(
      "online",
      handleOnline
    );

    window.addEventListener(
      "pageshow",
      handlePageShow
    );

    window.addEventListener(
      "storage",
      handleStorage
    );

    return () => {
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }

      if (
        driversPageRefreshTimerRef.current
      ) {
        clearTimeout(
          driversPageRefreshTimerRef.current
        );

        driversPageRefreshTimerRef.current =
          null;
      }

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      window.removeEventListener(
        "focus",
        handleWindowFocus
      );

      window.removeEventListener(
        "online",
        handleOnline
      );

      window.removeEventListener(
        "pageshow",
        handlePageShow
      );

      window.removeEventListener(
        "storage",
        handleStorage
      );
    };
  }, [
    operationsCacheKey,
    scheduleDriversPageRefresh,
  ]);

  /* ================================ Handlers ============================== */
  const openDriverDeleteModal = (
    driver,
    activeLoadsCount = 0
  ) => {
    if (!driver?.id) {
      return;
    }

    setErrorMessage("");
    setDeleteError("");
    setDeleteConfirmation("");

    setDriverPendingRemoval({
      id: Number(driver.id),
      name:
        driver.name ||
        t("table.na", "N/D"),
      activeLoadsCount:
        Number(activeLoadsCount) || 0,
    });
  };

  const handleRemoveDriver = async () => {
    const driverId =
      Number(driverPendingRemoval?.id);

    if (
      !Number.isInteger(driverId) ||
      driverId <= 0 ||
      !deleteConfirmationMatches ||
      deleteLoading
    ) {
      return;
    }

    setDeleteLoading(true);
    setDeleteError("");

    try {
      await axios.delete(
        `/driveroriginal/${driverId}`,
        {
          data: {
            confirmation:
              deleteConfirmation.trim(),
          },
        }
      );

      setDrivers((previousDrivers) => {
        const currentDrivers =
          Array.isArray(previousDrivers)
            ? previousDrivers
            : [];

        const nextDrivers =
          currentDrivers.filter(
            (driver) =>
              Number(driver.id) !== driverId
          );

        /*
         * Evita que a versão estável volte
         * temporariamente a mostrar o motorista.
         */
        stableDriversRef.current =
          nextDrivers;

        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              ts: Date.now(),
              items: nextDrivers,
            })
          );
        } catch {
          /*
           * A remoção continua válida mesmo
           * sem localStorage.
           */
        }

        return nextDrivers;
      });

      setDriverLoadLabelsById(
        (previousOperations) => {
          const nextOperations = {
            ...(previousOperations || {}),
          };

          delete nextOperations[driverId];

          writeDriverOperationsCache(
            operationsCacheKey,
            nextOperations
          );

          return nextOperations;
        }
      );

      setDriverPendingRemoval(null);
      setDeleteConfirmation("");
      setDeleteError("");
    } catch (error) {
      setDeleteError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          t(
            "errors.deleteFailed",
            "Não foi possível remover o motorista."
          )
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditDriver = async (
    arg
  ) => {
    const id =
      typeof arg === "object"
        ? arg?.id
        : arg;

    if (!id) {
      console.warn(
        "[Drivers] handleEditDriver: missing id",
        arg
      );

      return;
    }

    let sourceRow =
      typeof arg === "object"
        ? arg
        : driversForRender.find(
            (driver) =>
              Number(driver.id) ===
              Number(id)
          );

    /*
     * Caminho normal: o summary já trouxe tudo e a
     * navegação acontece imediatamente.
     *
     * Este fallback serve apenas para uma cache antiga
     * criada antes desta alteração. Nesse caso, ficamos
     * na lista até o detalhe chegar, evitando abrir o
     * EditDriver com campos vazios.
     */
    if (
      sourceRow?.__driverDetailsComplete !==
      true
    ) {
      try {
        let response;

        try {
          response =
            await axios.get(
              `/driveroriginal/${id}`
            );
        } catch {
          response =
            await axios.get(
              `/drivers/${id}`
            );
        }

        sourceRow = {
          ...(response?.data?.driver ??
            response?.data ??
            sourceRow ??
            {}),

          __driverDetailsComplete:
            true,
        };
      } catch (error) {
        console.warn(
          "[Drivers] failed to fetch complete driver before edit",
          {
            driverId:
              id,

            status:
              error?.response?.status,

            message:
              error?.message,
          }
        );
      }
    }

    /*
     * buildEditPrefillFromListRow permanece como fallback
     * para o modelo antigo. O spread de sourceRow vem depois
     * para nenhum valor vazio apagar os dados completos.
     */
    const prefill = {
      id,

      ...buildEditPrefillFromListRow(
        sourceRow || {
          id,
        }
      ),

      ...(sourceRow || {}),

      __driverDetailsComplete:
        sourceRow?.__driverDetailsComplete ===
        true,
    };

    primeDriverDetailsCache(
      id,
      prefill
    );

    navigate(
      `/drivers/${id}/edit`,
      {
        state: {
          prefill,
        },
      }
    );
  };

  const handleInviteNickname = async (nickname) => {
    setNickError("");
    setNickSuccess("");
    setNickLoading(true);
    try {
      const { data } = await axios.post("/notifications4driver", {
        nickname,
        notificationType: "Invitation",
      });
      setNickOpen(false);
      if (data?.already_member) {
        setNickSuccess(t("invite.alreadyMember", { nickname }));
      } else {
        setNickSuccess(t("invite.invited", { nickname }));
      }
      const cid = resolveCompanyId();
      const { data: list } = await axios.get("/driveroriginal/summary", {
        params: { company_id: cid ?? undefined, limit: 500 },
      });
      const rows = Array.isArray(list?.rows)
        ? list.rows
        : Array.isArray(list)
        ? list
        : [];
      setDrivers(rows.map(mapDriverOriginalToUI));
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "already_member") {
        setNickSuccess(t("invite.alreadyMember", { nickname }));
        try {
          const cid = resolveCompanyId();
          const { data: list } = await axios.get("/driveroriginal/summary", {
            params: { company_id: cid ?? undefined, limit: 500 },
          });
          const rows = Array.isArray(list?.rows)
            ? list.rows
            : Array.isArray(list)
            ? list
            : [];
          setDrivers(rows.map(mapDriverOriginalToUI));
        } catch {
          /* ignore refresh error */
        }
        setNickOpen(false);
      } else {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          t("errors.inviteFailedDefault");
        setNickError(msg);
      }
    } finally {
      setNickLoading(false);
    }
  };
  useEffect(() => {
    if (isSearching) return;
    if (!sentinelRef.current) return;
    if (visibleLimit >= visibleCount) return;
    const rootEl = tableRef.current || null;
    const ioObs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleLimit((v) => Math.min(v + PAGE_SIZE, visibleCount));
        }
      },
      {
        root: rootEl,
        rootMargin: "220px 0px",
        threshold: 0.01,
      }
    );
    ioObs.observe(sentinelRef.current);
    return () => ioObs.disconnect();
  }, [isSearching, visibleLimit, visibleCount]);
  /* ================================= Render =============================== */
  return (
    <div className="drivers-page">
      <section className="drivers-card drivers-toolbar">
        <div className="drivers-toolbar__left">
          <div className="drivers-toolbar-heading">
            <h1 className="drivers-toolbar-heading__title">
              {t("header.title", "Motoristas")}
            </h1>
            <p className="drivers-toolbar-heading__subtitle">
              {t("header.registered", {
                count: totalCount,
                defaultValue: "{{count}} motoristas registados",
              })}
            </p>
          </div>
        </div>
        <div className="drivers-toolbar__right">
          <div className="drivers-search">
            <FaSearch className="drivers-search__icon" />
            <input
              type="text"
              className="drivers-search__input"
              placeholder={t("search.placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label={t("search.ariaLabel")}
            />
            {searchTerm && (
              <button
                type="button"
                className="drivers-search__clear"
                onClick={() => setSearchTerm("")}
                aria-label={t("search.clearAria")}
                title={t("search.clearTitle")}
              >
                ✕
              </button>
            )}
          </div>
          <button
            className="drivers-new-btn drivers-btn--primary"
            onClick={() => setNickOpen(true)}
            type="button"
          >
            {t("actions.newDriver")}
          </button>
        </div>
      </section>
      {errorMessage && (
        <section className="drivers-card drivers-error">
          <div className="drivers-error__text">{errorMessage}</div>
        </section>
      )}
      {nickSuccess ? (
        <div className="drivers-toast drivers-toast--success" role="status" aria-live="polite">
          {nickSuccess}
        </div>
      ) : null}
      <section className="drivers-list-shell">
        <div ref={tableRef} className="drivers-grid">
          {baseTableRendered ? (
            visibleCount > 0 ? (
              <>
                {visibleDrivers.map((driver, driverIndex) => {
                  const displayName = driver.name || t("table.na", "N/D");
                  const displayPhone = driver.contact_number || t("table.na", "N/D");
                  const displayLicense = driver.driver_license || t("table.na", "N/D");
                  const displayTacho = driver.driver_tacho_number || t("table.na", "N/D");
                  const displayTruck = valueToDisplayText(driver.assigned_truck);
                  const displayTrailer = valueToDisplayText(driver.assigned_trailer);
                  const currentLoads = Number(driver.currentLoads ?? 0);
                  const driverLoads = driverLoadLabelsById[Number(driver.id)] || [];
                  const activeLoadsCount =
                    driverLoads.length > 0 ? driverLoads.length : currentLoads;
                  const activeOperations = driverLoads
                    .flatMap((load, loadIdx) => {
                      const toneClass = getDriverLoadToneClass(load?.loadId, loadIdx);
                      const loadBadgeLabel = getDriverLoadBadgeLabel(load, loadIdx);
                      const normalizedPoints = Array.isArray(load.points)
                        ? load.points
                            .map((point) => {
                              const type = String(point?.type || point?.pointType || "").toUpperCase();
                              const label = String(
                                point?.label ||
                                  point?.location ||
                                  point?.placeName ||
                                  point?.pickupLocation ||
                                  point?.deliveryLocation ||
                                  point?.address ||
                                  ""
                              ).trim();
                              return {
                                ...point,
                                type,
                                label,
                                date: point?.date || point?.pickupDate || point?.deliveryDate || "",
                                hour: point?.hour || point?.pickupHour || point?.deliveryHour || "",
                                loadId: Number(point?.loadId || load.loadId) || null,
                                loadNumber:
                                  point?.loadNumber ||
                                  point?.loadRef ||
                                  load.loadNumber ||
                                  "",
                                clientName:
                                  point?.clientName ||
                                  point?.client ||
                                  load.clientName ||
                                  "",
                              };
                            })
                            .filter((point) => point.label)
                        : [];
                      const fallbackPoints = [];
                      if (!normalizedPoints.length && load.pickupLabel) {
                        fallbackPoints.push({
                          type: "PICKUP",
                          label: load.pickupLabel,
                          date: load.pickupDate || "",
                          hour: load.pickupHour || "",
                          loadId: Number(load.loadId) || null,
                          loadNumber: load.loadNumber || "",
                          clientName: load.clientName || "",
                        });
                      }
                      if (!normalizedPoints.length && load.deliveryLabel) {
                        fallbackPoints.push({
                          type: "DELIVERY",
                          label: load.deliveryLabel,
                          date: load.deliveryDate || "",
                          hour: load.deliveryHour || "",
                          loadId: Number(load.loadId) || null,
                          loadNumber: load.loadNumber || "",
                          clientName: load.clientName || "",
                        });
                      }
                      if (!normalizedPoints.length && !fallbackPoints.length && load.fallbackLabel) {
                        fallbackPoints.push({
                          type: "GENERIC",
                          label: load.fallbackLabel,
                          date: load.fallbackDate || "",
                          hour: load.fallbackHour || "",
                          loadId: Number(load.loadId) || null,
                          loadNumber: load.loadNumber || "",
                          clientName: load.clientName || "",
                        });
                      }
                      const pointsToRender = normalizedPoints.length ? normalizedPoints : fallbackPoints;
                      return pointsToRender.map((point, pointIdx) => {
                        const type = String(point.type || "").toUpperCase();
                        const operationClass =
                          type === "PICKUP"
                            ? "drivers-operation-row--pickup"
                            : type === "DELIVERY"
                            ? "drivers-operation-row--delivery"
                            : "drivers-operation-row--generic";
                        const operationTypeLabel =
                          type === "PICKUP"
                            ? t("assignment.pickup", "Recolha")
                            : type === "DELIVERY"
                            ? t("assignment.delivery", "Entrega")
                            : t("assignment.strategicPoint", "Ponto Estratégico");
                        const hourLabel = point.hour ? String(point.hour).slice(0, 5) : "";
                        const dateTimeLabel = formatDriverJourneyDateTime(point.date, point.hour);
                        return {
                          ...point,
                          operationClass,
                          operationTypeLabel,
                          toneClass,
                          loadBadgeLabel,
                          sortIndex: `${loadIdx}-${pointIdx}`,
                          displayTime: hourLabel || dateTimeLabel || "—",
                          displayDateTime: dateTimeLabel,
                          loadRef: point.loadNumber || point.loadId || load.loadNumber || load.loadId || "N/D",
                          clientName: point.clientName || load.clientName || t("table.na", "N/D"),
                          rawStatus: String(
                            point?.status ||
                              point?.pointStatus ||
                              point?.operationStatus ||
                              ""
                          )
                            .trim()
                            .toUpperCase(),
                        };
                      });
                    })
                    .sort((a, b) => {
                      const dateCompare = compareOperationDateTime(a, b);
                      if (dateCompare !== 0) return dateCompare;
                      return String(a.sortIndex).localeCompare(String(b.sortIndex));
                    });
                  const compactLoadCards = driverLoads
                    .map((load, loadIdx) => {
                      const operations =
                        Array.isArray(load.points)
                          ? load.points
                              .map(
                                (
                                  point,
                                  pointIdx
                                ) => {
                                  const type =
                                    String(
                                      point?.type ||
                                        point?.pointType ||
                                        ""
                                    ).toUpperCase();

                                  const label =
                                    String(
                                      point?.label ||
                                        point?.location ||
                                        point?.placeName ||
                                        point?.pickupLocation ||
                                        point?.deliveryLocation ||
                                        point?.address ||
                                        ""
                                    ).trim();

                                  if (!label) {
                                    return null;
                                  }

                                  const normalizedPoint =
                                    {
                                      ...point,
                                      type,
                                      label,

                                      date:
                                        point?.date ||
                                        point?.pickupDate ||
                                        point?.deliveryDate ||
                                        "",

                                      hour:
                                        point?.hour ||
                                        point?.pickupHour ||
                                        point?.deliveryHour ||
                                        "",

                                      loadId:
                                        Number(
                                          point?.loadId ||
                                            load.loadId
                                        ) || null,

                                      loadNumber:
                                        point?.loadNumber ||
                                        point?.loadRef ||
                                        load.loadNumber ||
                                        "",

                                      clientName:
                                        point?.clientName ||
                                        point?.client ||
                                        load.clientName ||
                                        "",
                                    };

                                  const operationTypeLabel =
                                    type === "PICKUP"
                                      ? t(
                                          "assignment.collection",
                                          "RECOLHA"
                                        )
                                      : type ===
                                        "DELIVERY"
                                      ? t(
                                          "assignment.dropoff",
                                          "ENTREGA"
                                        )
                                      : t(
                                          "assignment.strategicPoint",
                                          "PONTO ESTRATÉGICO"
                                        );

                                  const rowClassName =
                                    type === "PICKUP"
                                      ? "drivers-compact-load-row--pickup"
                                      : type ===
                                        "DELIVERY"
                                      ? "drivers-compact-load-row--delivery"
                                      : "drivers-compact-load-row--generic";

                                  return {
                                    ...normalizedPoint,

                                    operationTypeLabel,
                                    rowClassName,

                                    statusMeta:
                                      getDriverOperationStatusMeta(
                                        normalizedPoint,
                                        t
                                      ),

                                    operationKey:
                                      normalizedPoint.id ||
                                      `${
                                        load.loadId ||
                                        loadIdx
                                      }-${type}-${pointIdx}`,
                                  };
                                }
                              )
                              .filter(Boolean)
                              .sort((a, b) => {
                                const dateCompare =
                                  compareOperationDateTime(
                                    a,
                                    b
                                  );

                                if (
                                  dateCompare !== 0
                                ) {
                                  return dateCompare;
                                }

                                const aSequence =
                                  a.sequence === null ||
                                  a.sequence ===
                                    undefined
                                    ? Number.MAX_SAFE_INTEGER
                                    : Number(
                                        a.sequence
                                      );

                                const bSequence =
                                  b.sequence === null ||
                                  b.sequence ===
                                    undefined
                                    ? Number.MAX_SAFE_INTEGER
                                    : Number(
                                        b.sequence
                                      );

                                if (
                                  aSequence !==
                                  bSequence
                                ) {
                                  return (
                                    aSequence -
                                    bSequence
                                  );
                                }

                                return (
                                  Number(a.id || 0) -
                                  Number(b.id || 0)
                                );
                              })
                          : [];

                      if (!operations.length) {
                        return null;
                      }

                      return {
                        loadId:
                          Number(load.loadId) ||
                          null,

                        loadNumber:
                          load.loadNumber || "",

                        clientName:
                          load.clientName || "",

                        operations,
                      };
                    })
                    .filter(Boolean)
                    .sort((a, b) => {
                      const aPoint =
                        a.operations[0] || {};

                      const bPoint =
                        b.operations[0] || {};

                      return compareOperationDateTime(
                        aPoint,
                        bPoint
                      );
                    });

                  const displayedOperationCount =
                    compactLoadCards.reduce(
                      (total, loadCard) =>
                        total +
                        loadCard.operations.length,
                      0
                    );

                  const isAvailable = currentLoads <= 0 && driverLoads.length <= 0;
                  return (
                    <article
                      key={driver.id}
                      className="drivers-profile-card"
                      onClick={() => {
                        handleEditDriver(
                          driver
                        );
                      }}
                      onKeyDown={(event) => {
                        if (
                          event.key ===
                          "Enter"
                        ) {
                          handleEditDriver(
                            driver
                          );
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <button
                        type="button"
                        className="drivers-card-delete"
                        title={t(
                          "actions.removeTitle",
                          "Remover motorista da empresa"
                        )}
                        aria-label={t(
                          "actions.removeAria",
                          "Remover motorista da empresa"
                        )}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();

                          openDriverDeleteModal(
                            driver,
                            activeLoadsCount
                          );
                        }}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      >
                        <FaTrashAlt />
                      </button>
                      <div className="drivers-card-hero">
                        <DriverAvatar
                          src={
                            driver.profile_photo_url
                          }
                          name={
                            displayName
                          }
                          isAvailable={
                            isAvailable
                          }
                          eager={
                            driverIndex <
                            PAGE_SIZE
                          }
                        />
                        <div className="drivers-card-title-block">
                          <h3 className="drivers-card-name" title={displayName}>
                            {displayName}
                          </h3>
                          <span
                            className={`drivers-status-badge ${
                              isAvailable
                                ? "drivers-status-badge--available"
                                : "drivers-status-badge--transit"
                            }`}
                          >
                            <FaTruck />
                            {isAvailable
                              ? t("status.available", "Disponível")
                              : t("status.transit", "Em Trânsito")}
                          </span>
                        </div>
                      </div>
                      <div className="drivers-info-panel">
                        <div className="drivers-info-grid">
                          <div className="drivers-info-box" title={displayPhone}>
                            <span className="drivers-info-icon drivers-info-icon--phone">
                              <FaPhoneAlt />
                            </span>
                            <span className="drivers-info-copy">
                              <span className="drivers-info-label">
                                {t("table.headers.phone", "Telemóvel")}
                              </span>
                              <span
                                className={`drivers-info-value ${
                                  !driver.contact_number ? "is-muted" : ""
                                }`}
                              >
                                {displayPhone}
                              </span>
                            </span>
                          </div>
                          <div className="drivers-info-box" title={displayLicense}>
                            <span className="drivers-info-icon">
                              <FaIdCard />
                            </span>
                            <span className="drivers-info-copy">
                              <span className="drivers-info-label">
                                {t("table.headers.license", "Carta condução")}
                              </span>
                              <span
                                className={`drivers-info-value ${
                                  !driver.driver_license ? "is-muted" : ""
                                }`}
                              >
                                {displayLicense}
                              </span>
                            </span>
                          </div>
                          <div className="drivers-info-box" title={displayTacho}>
                            <span className="drivers-info-icon">
                              <FaTachometerAlt />
                            </span>
                            <span className="drivers-info-copy">
                              <span className="drivers-info-label">
                                {t("table.headers.tacho", "Tacógrafo")}
                              </span>
                              <span
                                className={`drivers-info-value ${
                                  !driver.driver_tacho_number ? "is-muted" : ""
                                }`}
                              >
                                {displayTacho}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="drivers-vehicle-grid">
                          <div className="drivers-vehicle-box">
                            <span className="drivers-vehicle-icon drivers-vehicle-icon--truck">
                              <FaTruck />
                            </span>
                            <span className="drivers-vehicle-copy">
                              <span className="drivers-vehicle-label">
                                {t("assignment.tractor", "Trator")}
                              </span>
                              <span
                                className={`drivers-vehicle-value ${
                                  !displayTruck ? "is-muted" : ""
                                }`}
                                title={displayTruck || t("table.na", "N/D")}
                              >
                                {displayTruck || t("table.na", "N/D")}
                              </span>
                            </span>
                          </div>
                          <div className="drivers-vehicle-box">
                            <span className="drivers-vehicle-icon drivers-vehicle-icon--trailer">
                              <Container size={18} />
                            </span>
                            <span className="drivers-vehicle-copy">
                              <span className="drivers-vehicle-label">
                                {t("assignment.trailer", "Reboque")}
                              </span>
                              <span
                                className={`drivers-vehicle-value ${
                                  !displayTrailer ? "is-muted" : ""
                                }`}
                                title={displayTrailer || t("table.na", "N/D")}
                              >
                                {displayTrailer || t("table.na", "N/D")}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="drivers-operations-panel drivers-active-loads-panel">
                        <div className="drivers-operations-head">
                          <div className="drivers-operations-title">
                            <FaCalendarAlt />
                            <span>{t("assignment.todayOperations", "Operações")}</span>
                          </div>
                          <div className="drivers-operations-summary">
                            <strong>
                              {activeLoadsCount}{" "}
                              {activeLoadsCount === 1
                                ? t("assignment.loadSingular", "carga")
                                : t("assignment.loadPlural", "cargas")}
                            </strong>
                            <span />
                            <em>
                              {displayedOperationCount}{" "}
                              {displayedOperationCount === 1
                                ? t("assignment.operationSingular", "operação")
                                : t("assignment.operationPlural", "operações")}
                            </em>
                          </div>
                        </div>
                        {compactLoadCards.length > 0 ? (
                          <div className="drivers-compact-loads-list">
                            {compactLoadCards.map(
                              (loadCard, idx) => {
                                const loadKey =
                                  `${
                                    driver.id
                                  }-compact-load-${
                                    loadCard.loadId ||
                                    idx
                                  }`;

                                const routeTitle =
                                  loadCard.operations
                                    .map(
                                      (
                                        operation
                                      ) =>
                                        operation.label
                                    )
                                    .filter(Boolean)
                                    .join(" → ");

                                const firstOperation =
                                  loadCard.operations[0];

                                const lastOperation =
                                  loadCard.operations[
                                    loadCard
                                      .operations
                                      .length - 1
                                  ];

                                const firstDotClass =
                                  firstOperation?.type ===
                                  "PICKUP"
                                    ? "drivers-compact-load-dot--pickup"
                                    : "drivers-compact-load-dot--delivery";

                                const lastDotClass =
                                  lastOperation?.type ===
                                  "PICKUP"
                                    ? "drivers-compact-load-dot--pickup"
                                    : "drivers-compact-load-dot--delivery";

                                return (
                                  <div
                                    key={loadKey}
                                    className="drivers-compact-load-card"
                                    title={
                                      routeTitle
                                    }
                                  >
                                    <div className="drivers-compact-load-inner">
                                      <div
                                        className="drivers-compact-load-rail"
                                        aria-hidden="true"
                                      >
                                        <span
                                          className={`drivers-compact-load-dot ${firstDotClass}`}
                                        />

                                        {loadCard
                                          .operations
                                          .length >
                                        1 ? (
                                          <>
                                            <span className="drivers-compact-load-line" />

                                            <span
                                              className={`drivers-compact-load-dot ${lastDotClass}`}
                                            />
                                          </>
                                        ) : null}
                                      </div>

                                      <div className="drivers-compact-load-route">
                                        {loadCard.operations.map(
                                          (
                                            operation,
                                            operationIndex
                                          ) => {
                                            const isPickup =
                                              operation.type ===
                                              "PICKUP";

                                            const isDelivery =
                                              operation.type ===
                                              "DELIVERY";

                                            return (
                                              <React.Fragment
                                                key={`${loadKey}-row-${operation.operationKey}`}
                                              >
                                                {operationIndex >
                                                0 ? (
                                                  <div className="drivers-compact-load-divider" />
                                                ) : null}

                                                <div
                                                  className={`drivers-compact-load-row ${operation.rowClassName}`}
                                                >
                                                  <div className="drivers-compact-load-copy">
                                                    <span
                                                      className={`drivers-compact-load-kicker ${
                                                        isPickup
                                                          ? "drivers-compact-load-kicker--pickup"
                                                          : ""
                                                      }`}
                                                    >
                                                      {
                                                        operation.operationTypeLabel
                                                      }
                                                    </span>

                                                    <strong>
                                                      {
                                                        operation.label
                                                      }
                                                    </strong>

                                                    <small>
                                                      {formatDriverJourneyDateTime(
                                                        operation.date,
                                                        operation.hour
                                                      ) ||
                                                        t(
                                                          "assignment.dateToDefine",
                                                          "Data por definir"
                                                        )}
                                                    </small>
                                                  </div>

                                                  <div className="drivers-compact-load-side">
                                                    {operation.statusMeta ? (
                                                      <>
                                                        <span
                                                          className={`drivers-operation-status-pill ${operation.statusMeta.statusClassName}`}
                                                        >
                                                          {
                                                            operation
                                                              .statusMeta
                                                              .statusIcon
                                                          }

                                                          {
                                                            operation
                                                              .statusMeta
                                                              .statusLabel
                                                          }
                                                        </span>

                                                        <small className="drivers-operation-status-subtitle">
                                                          {
                                                            operation
                                                              .statusMeta
                                                              .statusSubtitle
                                                          }
                                                        </small>
                                                      </>
                                                    ) : null}
                                                  </div>

                                                  <div className="drivers-operation-point-action-wrap">
                                                    <span
                                                      className={`drivers-operation-point-action ${
                                                        isPickup
                                                          ? "drivers-operation-point-action--pickup"
                                                          : "drivers-operation-point-action--delivery"
                                                      }`}
                                                    >
                                                      {isPickup ? (
                                                        <FaMapMarkerAlt />
                                                      ) : isDelivery ? (
                                                        <FaFlag />
                                                      ) : (
                                                        <FaMapMarkerAlt />
                                                      )}
                                                    </span>
                                                  </div>
                                                </div>
                                              </React.Fragment>
                                            );
                                          }
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        ) : activeLoadsCount > 0 ? (
                          <div className="drivers-operations-empty">
                            {activeLoadsCount === 1
                              ? t("assignment.assignedLoadFallback", "1 carga atribuída")
                              : t("assignment.assignedLoadsFallback", "{{count}} cargas atribuídas", {
                                  count: activeLoadsCount,
                                })}
                          </div>
                        ) : (
                          <div className="drivers-operations-empty">
                            {t("assignment.noLoad", "Sem cargas atribuídas")}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
                {!isSearching && renderedCount < visibleCount && (
                  <div ref={sentinelRef} className="drivers-infinite-sentinel" />
                )}
              </>
            ) : (
              <div className="drivers-empty">
                <div className="drivers-empty__title">
                  {isSearching
                    ? t("empty.noSearchResults", "Nenhum motorista encontrado")
                    : t("empty.title", "Ainda não existem motoristas")}
                </div>
                <div className="drivers-empty__subtitle">
                  {isSearching
                    ? t(
                        "empty.noSearchResultsSubtitle",
                        "Experimenta pesquisar por outro nome ou limpa a pesquisa."
                      )
                    : t(
                        "empty.subtitle",
                        "Convida o primeiro motorista para começar a gerir a tua equipa."
                      )}
                </div>
                {isSearching ? (
                  <button
                    className="drivers-btn drivers-btn--primary"
                    onClick={() => setSearchTerm("")}
                    type="button"
                  >
                    {t("search.clearTitle", "Limpar pesquisa")}
                  </button>
                ) : (
                  <button
                    className="drivers-btn drivers-btn--primary"
                    onClick={() => setNickOpen(true)}
                    type="button"
                  >
                    {t("actions.addDriver", "Adicionar motorista")}
                  </button>
                )}
              </div>
            )
          ) : (
            <div className="drivers-loading" role="status" aria-live="polite">
              {t("common.loading", "A carregar...")}
            </div>
          )}
        </div>
      </section>
      <DriverNicknameInput
        isOpen={nickOpen}
        onClose={() => setNickOpen(false)}
        onConfirm={handleInviteNickname}
        loading={nickLoading}
      />

      {driverPendingRemoval ? (
        <div
          className="drivers-delete-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              closeDriverDeleteModal();
            }
          }}
        >
          <section
            className="drivers-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="drivers-delete-modal-title"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
          >
            <header className="drivers-delete-modal__header">
              <div className="drivers-delete-modal__heading">
                <span className="drivers-delete-modal__icon">
                  <FaTrashAlt />
                </span>

                <div>
                  <span className="drivers-delete-modal__eyebrow">
                    {t(
                      "deleteModal.confirmationRequired",
                      "Confirmação obrigatória"
                    )}
                  </span>

                  <h2 id="drivers-delete-modal-title">
                    {t(
                      "deleteModal.title",
                      "Remover motorista da empresa"
                    )}
                  </h2>
                </div>
              </div>

              <button
                type="button"
                className="drivers-delete-modal__close"
                aria-label={t(
                  "deleteModal.close",
                  "Fechar"
                )}
                title={t(
                  "deleteModal.close",
                  "Fechar"
                )}
                disabled={deleteLoading}
                onClick={
                  closeDriverDeleteModal
                }
              >
                ×
              </button>
            </header>

            <div className="drivers-delete-modal__body">
              <p className="drivers-delete-modal__intro">
                <Trans
                  ns="drivers"
                  i18nKey="deleteModal.intro"
                  values={{
                    name:
                      driverPendingRemoval.name,
                  }}
                  components={{
                    driverName: <strong />,
                  }}
                >
                  Está prestes a remover{" "}
                  <driverName>
                    {
                      driverPendingRemoval.name
                    }
                  </driverName>{" "}
                  da sua empresa.
                </Trans>
              </p>

              <div className="drivers-delete-modal__scope">
                <strong>
                  {t(
                    "deleteModal.accountNotDeletedTitle",
                    "A conta LOADEvo do motorista não será apagada."
                  )}
                </strong>

                <span>
                  {t(
                    "deleteModal.accountNotDeletedDescription",
                    "Será removida apenas a ligação entre este motorista e a sua empresa."
                  )}
                </span>
              </div>

              {driverPendingRemoval.activeLoadsCount >
              0 ? (
                <div className="drivers-delete-modal__warning">
                  <FaCalendarAlt />

                  <div>
                    <strong>
                      {driverPendingRemoval.activeLoadsCount ===
                      1
                        ? t(
                            "deleteModal.activeLoadsSingular",
                            {
                              count:
                                driverPendingRemoval.activeLoadsCount,
                              defaultValue:
                                "Este motorista tem {{count}} carga atribuída.",
                            }
                          )
                        : t(
                            "deleteModal.activeLoadsPlural",
                            {
                              count:
                                driverPendingRemoval.activeLoadsCount,
                              defaultValue:
                                "Este motorista tem {{count}} cargas atribuídas.",
                            }
                          )}
                    </strong>

                    <span>
                      {t(
                        "deleteModal.reviewAssignments",
                        "As operações existentes devem ser revistas e desatribuídas manualmente."
                      )}
                    </span>
                  </div>
                </div>
              ) : null}

              <label
                className="drivers-delete-confirm-label"
                htmlFor="drivers-delete-confirm-input"
              >
                <Trans
                  ns="drivers"
                  i18nKey="deleteModal.typeDelete"
                  components={{
                    confirmation: <code />,
                  }}
                >
                  Para confirmar, escreva{" "}
                  <confirmation>
                    DELETE
                  </confirmation>
                </Trans>
              </label>

              <input
                ref={
                  deleteConfirmationInputRef
                }
                id="drivers-delete-confirm-input"
                type="text"
                className={`drivers-delete-confirm-input ${
                  deleteConfirmationMatches
                    ? "is-valid"
                    : ""
                }`}
                value={deleteConfirmation}
                placeholder="DELETE"
                aria-label={t(
                  "deleteModal.confirmationInputAria",
                  "Escreva DELETE para confirmar"
                )}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                disabled={deleteLoading}
                onChange={(event) => {
                  setDeleteConfirmation(
                    event.target.value
                  );

                  if (deleteError) {
                    setDeleteError("");
                  }
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    deleteConfirmationMatches &&
                    !deleteLoading
                  ) {
                    event.preventDefault();
                    handleRemoveDriver();
                  }
                }}
              />

              <div className="drivers-delete-confirm-status">
                {deleteConfirmationMatches ? (
                  <span className="is-valid">
                    {t(
                      "deleteModal.confirmationValidated",
                      "Confirmação validada"
                    )}
                  </span>
                ) : (
                  <span>
                    {t(
                      "deleteModal.confirmationHint",
                      "O botão ficará disponível depois de escrever DELETE."
                    )}
                  </span>
                )}
              </div>

              {deleteError ? (
                <div
                  className="drivers-delete-modal__error"
                  role="alert"
                >
                  {deleteError}
                </div>
              ) : null}
            </div>

            <footer className="drivers-delete-modal__footer">
              <button
                type="button"
                className="drivers-delete-modal__cancel"
                disabled={deleteLoading}
                onClick={
                  closeDriverDeleteModal
                }
              >
                {t(
                  "deleteModal.cancel",
                  "Cancelar"
                )}
              </button>

              <button
                type="button"
                className="drivers-delete-modal__confirm"
                disabled={
                  !deleteConfirmationMatches ||
                  deleteLoading
                }
                onClick={handleRemoveDriver}
              >
                <FaTrashAlt />

                {deleteLoading
                  ? t(
                      "deleteModal.removing",
                      "A remover..."
                    )
                  : t(
                      "deleteModal.removeDriver",
                      "Remover motorista"
                    )}
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {nickError ? (
        <section className="drivers-card drivers-error">
          <div className="drivers-error__text">{nickError}</div>
        </section>
      ) : null}
    </div>
  );
}
export default Drivers;
